/**
 * Create/update mutation for the admin user dialog, plus the organization
 * membership chain that used to be split across NewUser/EditUser.
 *
 * Call order is preserved from the MUI dialogs (edit: remove every current
 * membership, then add the selected one). Deliberate changes: the chain is
 * awaited inside one mutationFn so pending/success/error describe the whole
 * save, invalidation runs on settle so a partly applied chain still refreshes,
 * and an edit with no current and no selected organization no longer reads
 * `organizations[0].id` off undefined.
 */
import {
    addOrganizationMembers,
    createUser,
    removeOrganizationMembers,
    updateUser,
    type IApiAxiosError,
    type IOrganization,
    type IUser,
} from "@/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export interface SaveUserInput {
    first_name: string;
    last_name: string;
    email: string;
    is_admin: boolean;
    organization: IOrganization | null;
}

export function useSaveUser(user?: IUser | null) {
    const queryClient = useQueryClient();

    return useMutation<IUser, IApiAxiosError, SaveUserInput>({
        mutationFn: async ({ organization, ...fields }) => {
            const payload = { ...fields, organizations: organization };

            if (!user) {
                const created = await createUser(payload);
                if (organization) {
                    await addOrganizationMembers(organization.id, {
                        user_ids: [created.id],
                    });
                }
                return created;
            }

            const updated = await updateUser(user.id, payload);
            if (organization) {
                await Promise.all(
                    updated.organizations.map((current) =>
                        removeOrganizationMembers(current.id, [updated.id])
                    )
                );
                await addOrganizationMembers(organization.id, {
                    user_ids: [updated.id],
                });
            } else if (updated.organizations[0]) {
                await removeOrganizationMembers(updated.organizations[0].id, [
                    updated.id,
                ]);
            }
            return updated;
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-users"] });
            queryClient.invalidateQueries({
                queryKey: ["admin-organization-members"],
            });
            if (user) {
                queryClient.invalidateQueries({
                    queryKey: ["organization-members"],
                });
                queryClient.invalidateQueries({
                    queryKey: ["admin-user-details", user.id],
                });
            }
        },
    });
}
